import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Checkbox, TextInput } from "@primer/react";
import { XIcon, TrashIcon } from "@primer/octicons-react";
import TextWithEllipse from "../../../components/ui/text-with-ellipse/TextWithEllipse";

import { importHistoryApi } from "../../../api/importHistoryApi";
import Button from "../../../components/ui/button";
import MyModal from "../../../components/ui/modal/MyModal";
import ModalActions from "../../../components/ui/modal/ModalActions";
import DataGrid from "../../../components/data-grid";
import { IDataGridColumn } from "../../../components/data-grid/type/IDataGridProps";
import { sf_import_history_request } from "../../../models/request/import-history/sf_import_history_request";
import { mucPhaiThuApi } from "../../../api/mucPhaiThuApi";
import { showNotify } from "../../../helpers/toast";
import { sf_mucphaithu_select_byhocsinh_rq } from "../../../models/request/thu-phi/sf_mucphaithu_select_byhocsinh_rq";
import { hocSinhApi } from "../../../api/hocSinhApi";
import ThuPhiChiTiet from "../../../components/thu-phi/chi-tiet";
import { RootState } from "../../../state/reducers";
import TextAreaAutoHeight from "../../../components/primer-ui/text-area-auto-height";

type EditKhoanNopModalProps = {
    title?: string,
    onClose: () => void,
    request: sf_import_history_request
}

const EditKhoanNopModal = (props: EditKhoanNopModalProps) => {
    const [dataSource, setDataSource] = useState([])
    const [showHuyChungTuConfirm, setShowHuyChungTuConfirm] = useState<boolean>(false);
    const [isShowThuPhiChiTiet, setIsShowThuPhiChiTiet] = useState<boolean>(false);
    const [sf_mucphaithu_editing, setSf_mucphaithu_editing] = useState<any>(undefined);
    const [ts_hocsinh_viewmodel, setTs_hocsinh_viewmodel] = useState<any>();
    const [ly_do_huy, set_ly_do_huy] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedRowsKey, setSelectedRowsKey] = useState<any[]>([]);

    const { nam_hoc } = useSelector(
        (x: RootState) => x.common
    );

    const handleReloadData = async () => {
        const res = await importHistoryApi.select_by_function(props.request);
        if (res.is_success) {
            setDataSource(res.data)
        }
    }

    const onCloseForm = useCallback(() => {
        props.onClose();
    }, [props.onClose]);

    const handleHuyChungTuMultiple = async () => {
        setLoading(true)
        if (selectedRowsKey && selectedRowsKey.length > 0) {
            const datasend = { sf_mucphaithu_ids: selectedRowsKey, ly_do_huy: ly_do_huy };
            const res = await mucPhaiThuApi.huy_chung_tu_multiple(datasend);
            if (res.is_success) {
                setShowHuyChungTuConfirm(false)
                setLoading(false)
                set_ly_do_huy("")
                showNotify({ message: "Hủy chứng từ thành công", type: "success" })
                handleReloadData();
                setSelectedRowsKey([]);
            }
            else {
                setLoading(false)
                showNotify({ message: "Hủy chứng từ không thành công", type: "warning" })
            }
        }
    };

    const handleRealoadHocSinh = async (hocSinhId: number) => {
        const datasend: sf_mucphaithu_select_byhocsinh_rq = { ts_hocsinh_id: hocSinhId, nam_hoc: nam_hoc }
        const res = await hocSinhApi.selectByIdNamHoc(datasend);
        if (res.is_success) {
            setTs_hocsinh_viewmodel(res.data);
        }
    };

    useEffect(() => {
        handleReloadData();
    }, [])

    const columns: IDataGridColumn[] = useMemo(() => [

        {
            dataField: "ma_hs",
            caption: "Mã học sinh",
            fixed: "left",
            width: 100
        },
        {
            dataField: "ho_ten",
            caption: "Họ tên",
            width: 150,
            fixed: "left",
            cellRender: (data) => <TextWithEllipse text={data.ho_ten} lineNumber={1} />
        },
        {
            dataField: "ten_truong",
            caption: "Trường",
            width: 200,
            cellRender: (data) => <TextWithEllipse text={data.ten_truong} lineNumber={1} />
        },
        {
            dataField: "ten_khoi",
            caption: "Khối",
            width: 100
        },
        { dataField: "ten_he", caption: "Hệ", width: 150 },
        { dataField: "ten_lop", caption: "Lớp", width: 100 },

        {
            dataField: "so_chung_tu",
            caption: "Số chứng từ",
            fixed: 'left',
            width: 150,
            cellRender: (data: any) => (
                <a
                    style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
                    onClick={async () => {
                        setSf_mucphaithu_editing(data);
                        setTs_hocsinh_viewmodel(undefined);
                        handleRealoadHocSinh(data.ts_hocsinh_id);
                        setIsShowThuPhiChiTiet(true);
                    }}
                >
                    {data.so_chung_tu}
                </a>
            )
        },
        {
            dataField: "is_huy",
            caption: "Đã hủy",
            width: 80,
            fixed: "left",
            cellRender: (data) => (
                <Checkbox
                    checked={data.is_huy}
                />
            )
        },
        {
            dataField: "noi_dung",
            caption: "Nội dung",
            width: 200,
            cellRender: (data) => <TextWithEllipse text={data.noi_dung} lineNumber={1} />
        },
        {
            caption: "Tổng tiền",
            align: "center",
            columns: [
                {
                    dataField: "tong_tien_dinh_muc",
                    caption: "Định mức",
                    width: 120,
                    align: "right",
                    cellRender: (data) => (data.tong_tien_dinh_muc || 0).toLocaleString()
                },
                {
                    dataField: "tong_tien_mien_giam",
                    caption: "Miễn giảm",
                    width: 120,
                    align: "right",
                    cellRender: (data) => (data.tong_tien_mien_giam || 0).toLocaleString()
                },
                {
                    dataField: "tong_tien_phai_nop",
                    caption: "Phải nộp",
                    width: 120,
                    align: "right",
                    cellRender: (data) => (data.tong_tien_phai_nop || 0).toLocaleString()
                },
                {
                    dataField: "tong_tien_da_nop",
                    caption: "Đã nộp",
                    width: 120,
                    align: "right",
                    cellRender: (data) => (data.tong_tien_da_nop || 0).toLocaleString()
                }
            ]
        }
    ], [nam_hoc]);

    return (
        <MyModal
            width={'95%'}
            title={props.title}
            onClose={onCloseForm}
            isOpen={true}
        >
            <div className="pop-up-form">
                <DataGrid
                    keyExpr="id"
                    dataSource={dataSource}
                    columns={columns}
                    height={window.innerHeight - 280}
                    paging={{ enable: true, pageSize: 50, pageSizeItems: [50, 100, 200, 500] }}
                    selection={{
                        mode: "multiple",
                        selectedRowKeys: selectedRowsKey,
                        onSelectionChanged: (keys) => setSelectedRowsKey(keys)
                    }}
                    filterRow={{ enable: true }}
                    searchEnable={true}
                />

                {showHuyChungTuConfirm && (
                    <MyModal
                        title="Hủy chứng từ đã import"
                        onClose={() => setShowHuyChungTuConfirm(false)}
                        isOpen={true}
                        width="60%"
                    >
                        <div className="pop-up-form" style={{ padding: '10px' }}>
                            <div className="form-group">
                                <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Lý do hủy:</label>
                                <TextAreaAutoHeight
                                    value={ly_do_huy}
                                    onValueChanged={(value) => set_ly_do_huy(value)}
                                    placeholder="Vui lòng nhập lý do hủy..."
                                    rows={5}
                                    block
                                />
                            </div>
                        </div>
                        <ModalActions>
                            <Button variant="default" leadingVisual={XIcon} onClick={() => setShowHuyChungTuConfirm(false)}>Đóng</Button>
                            <Button
                                variant="danger"
                                isLoading={loading}
                                leadingVisual={TrashIcon}
                                disabled={!ly_do_huy}
                                onClick={handleHuyChungTuMultiple}
                            >
                                Xác nhận hủy
                            </Button>
                        </ModalActions>
                    </MyModal>
                )}

                {isShowThuPhiChiTiet &&
                    sf_mucphaithu_editing &&
                    ts_hocsinh_viewmodel && (
                        <ThuPhiChiTiet
                            animationOf={`#btnChungTu${sf_mucphaithu_editing.id}`}
                            sf_mucphaithu={sf_mucphaithu_editing}
                            onClose={() => {
                                setIsShowThuPhiChiTiet(false);
                            }}
                            ts_hocsinh={ts_hocsinh_viewmodel}
                            nam_hoc={sf_mucphaithu_editing.nam_hoc}
                        />
                    )}
            </div>
            <ModalActions>
                <Button variant="default" leadingVisual={XIcon} onClick={onCloseForm}>Đóng</Button>
                <Button
                    variant="danger"
                    leadingVisual={TrashIcon}
                    disabled={selectedRowsKey.length === 0}
                    onClick={() => setShowHuyChungTuConfirm(true)}
                >
                    Hủy {selectedRowsKey.length} chứng từ đã chọn
                </Button>
            </ModalActions>
        </MyModal>
    );
}

export default EditKhoanNopModal;
